import { Component, EventEmitter, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from "@angular/core";
import { NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { EDeliveryService } from "../../services/e-delivery.service";
import { AvailableProduct, CartDetails } from "../../models/cart-details.model";
import { Helper } from "src/app/core/helpers/helper";
import { PromoCode } from "../../models/promo-code.model";
import { trigger, transition, style, animate } from "@angular/animations";
import { PaymentService } from "src/app/shared/services/payment.service";
import { FormGroup, FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { CommonModule } from "@angular/common";
import { ModalComponentsModule } from "src/app/shared/modal-components/modal-components.module";
import { BookingType, BusinessType } from "src/app/core/constants/bookingConstant";
import { Observable, Subscription } from "rxjs";
import { Cart } from "../../../q-commerce/models/cart.model";
import { CategoryGroupComponent } from "../../../q-commerce/components/category-group/category-group.component";
import { CategoryGroup, Modifier, Tag } from "../../../q-commerce/models/category-group.model";
import { MainCategory } from "../../../q-commerce/models/main-category.model";
import { QCommerceService } from "../../../q-commerce/services/q-commerce.service";
import { ServicesService } from "../../../service/services/services.service";
import { BookingPaymentModalComponent } from "src/app/shared/modal-components/booking-payment-modal/booking-payment-modal.component";
import { PromoCodeModalComponent } from "src/app/shared/modal-components/promo-code-modal/promo-code-modal.component";
import { BookForOtherModalComponent } from "src/app/shared/modal-components/book-for-other-modal/book-for-other-modal.component";
import { AccessibilityService } from "src/app/shared/services/accessibility.service";
import { Merchant } from "../../models/merchant.model";

@Component({
  selector: "app-cart-modal",
  templateUrl: "./cart-modal.component.html",
  styleUrl: "./cart-modal.component.scss",
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule, ModalComponentsModule, CategoryGroupComponent],
  animations: [
    trigger("quantityChangeAnimation", [
      transition(":increment", [style({ opacity: 0, transform: "scale(1.2)" }), animate("0.3s ease-in", style({ opacity: 1, transform: "scale(1)" }))]),
      transition(":decrement", [style({ opacity: 0, transform: "scale(1.2)" }), animate("0.5s ease-out", style({ opacity: 1, transform: "scale(1)" }))])
    ])
  ]
})
export class CartModalComponent implements OnInit, OnDestroy {
  cartItems: Cart[] = [];
  cartDetails!: CartDetails;
  tipPrices: number[] = [];
  isDeliveryActive: boolean = true;
  selectedPaymentMethod!: number;
  selectedTipPriceIndex!: number;
  citySetting: any;
  merchantSetting: any;
  manualTipAmount: number | null = null;
  deliveryAddressForm!: FormGroup;
  categoryGroups: CategoryGroup[] = [];
  appliedPromo!: PromoCode;
  cards: Array<any> = [];
  paymentOptions: Array<any> = [];
  isPaymentOptionError: boolean = false;
  isShowDiscountDetails: boolean = false;
  isShowDeliveryPriceDetails: boolean = false;
  availableWalletBalance: number = 0;
  customerNote: string = "";
  customerDetails: any;
  merchantId: string = "";
  typeId: string = "";
  entityType!: number;
  currentDeliveryAddress: any;
  isDeliveryBusiness!: boolean;
  isServicesBusiness!: boolean;
  cartItemSubscription!: Subscription;
  isCartModalOpen: boolean = false;
  selectedBookingType!: any;
  tags: Tag[] = [];
  serviceMainCategory!: MainCategory;
  isPickAtMyLocation!: boolean;
  isDropAtMyLocation!: boolean;
  isFromSavedAddress: boolean = false;
  customPrices: Array<any> = [];
  @ViewChild("bookForOtherModal", { static: true }) bookForOtherModal!: BookForOtherModalComponent;
  @ViewChild("promoCodeModal", { static: true }) promoCodeModal!: PromoCodeModalComponent;
  @ViewChild("bookingPaymentModal", { static: true }) bookingPaymentModal!: BookingPaymentModalComponent;
  @ViewChild("cartModal", { static: true }) cartModal!: TemplateRef<any>;
  @Output() onChangeServiceProvider = new EventEmitter();
  @Output() onAddOrSelectAddress = new EventEmitter();

  constructor(
    private offcanvasService: NgbOffcanvas,
    private edeliveryService: EDeliveryService,
    public helper: Helper,
    private paymentService: PaymentService,
    private qcommerceService: QCommerceService,
    private servicesService: ServicesService,
    private accessibilityService: AccessibilityService
  ) {}

  ngOnInit(): void {
    let cartItems$: Observable<Cart[]>;
    if (this.helper.route.url.includes("/users/create-booking/e-delivery")) {
      cartItems$ = this.edeliveryService.cartItems$;
    } else if (this.helper.route.url.includes("/users/create-booking/services")) {
      cartItems$ = this.servicesService.cartItems$;
    } else {
      cartItems$ = this.qcommerceService.cartItems$;
    }
    this.cartItemSubscription = cartItems$.subscribe(() => {
      if (this.isCartModalOpen) {
        this.getCartItems(this.tipPrices[this.selectedTipPriceIndex]);
      }
    });
  }

  /**
   * Calls the getCart method from the e-delivery service to get the cart details.
   * The parameters passed to the getCart method include the destination address,
   * products, merchantId, payment method, booking time, and booking type.
   * If a tip price is provided, it is added to the parameters.
   * If a promo code has been applied, it is added to the parameters.
   * The response from the getCart method is stored in the cartDetails property.
   * The selectedTipPriceIndex is set to the index of the tip price in the tipPrices array.
   * @param products The products to pass to the getCart method.
   * @param merchantId The merchantId to pass to the getCart method.
   * @param tipPrices The tip price to pass to the getCart method.
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity, complexity
  getCart(products: Cart[], merchantId: string, tipPrices?: number): void {
    const destinationAddress = JSON.parse(localStorage.getItem("currentDeliveryAddress") || "{}");
    let bookingTime = Number(localStorage.getItem("bookingTime"));

    if (this.isDeliveryBusiness && bookingTime && this.merchantSetting && this.merchantSetting.deliveryScheduleSetting.isActive) {
      bookingTime = new Date(new Date(new Date().getTime() + this.merchantSetting.deliveryScheduleSetting.userScheduleBuffer * 60000)).getTime();
    } else if (this.isServicesBusiness && bookingTime) {
      bookingTime = new Date(
        new Date(
          new Date().getTime() +
            this.citySetting.driverSetting[this.helper.BookingType[this.selectedBookingType]][this.helper.BookingType[this.helper.BookingType.SCHEDULE]]?.bufferTime * 60000
        )
      ).getTime();
    }
    if (!merchantId && this.helper.route.url.includes("/users/create-booking/quick-commerce")) {
      merchantId = localStorage.getItem("merchantId") || "";
    }
    const parameters: any = {
      destinationAddress: destinationAddress,
      paymentMode: this.selectedPaymentMethod,
      isShowSuccessToast: false
    };
    if (this.helper.route.url.includes("/users/create-booking/e-delivery") || this.helper.route.url.includes("/users/create-booking/quick-commerce")) {
      parameters["merchantId"] = merchantId;
    }
    if (this.helper.route.url.includes("/users/create-booking/e-delivery")) {
      parameters["bookingType"] = this.isDeliveryActive ? BookingType.DELIVERY : BookingType.PICKUP;
      if (this.merchantSetting && this.merchantSetting.deliveryScheduleSetting.isActive) {
        parameters["bookingTime"] = bookingTime || Date.now();
      } else {
        parameters["bookingTime"] = Date.now();
      }
    } else if (this.helper.route.url.includes("/users/create-booking/services")) {
      const cartServiceMerchantDetails: Merchant = JSON.parse(localStorage.getItem("cartServiceMerchantDetails") || "{}");
      parameters["bookingType"] = this.selectedBookingType;
      parameters["cityId"] = this.citySetting.cityId;
      parameters["mainCategoryId"] = this.serviceMainCategory._id;
      parameters["bookingTime"] = bookingTime || Date.now();
      if (this.selectedBookingType === this.helper.BookingType.ON_SITE_SERVICE) {
        parameters["isPickupFromHome"] = this.isPickAtMyLocation;
        parameters["isDropAtHome"] = this.isDropAtMyLocation;
      }
      if (cartServiceMerchantDetails && cartServiceMerchantDetails._id) {
        parameters["typeId"] = cartServiceMerchantDetails._id;
        parameters["entityType"] = this.helper.UserType.MERCHANT;
        parameters["isMerchantService"] = true;
      }
    }
    if (this.helper.route.url.includes("/users/create-booking/quick-commerce") || this.helper.route.url.includes("/users/create-booking/services")) {
      products.forEach((product) => {
        delete product.price;
        delete product.serviceMerchantId;
      });
      parameters["products"] = products;
    } else if (this.helper.route.url.includes("/users/create-booking/e-delivery")) {
      parameters["products"] = products;
    }
    if (
      (tipPrices && this.isDeliveryActive) ||
      (tipPrices && this.helper.route.url.includes("/users/create-booking/quick-commerce")) ||
      (tipPrices && this.helper.route.url.includes("/users/create-booking/services"))
    ) {
      parameters["tipPrice"] = tipPrices;
    }
    if (this.appliedPromo && this.appliedPromo.code) {
      parameters["promoCode"] = this.appliedPromo.code;
    }
    if (this.typeId && this.entityType) {
      parameters["typeId"] = this.typeId;
      parameters["entityType"] = this.entityType;
    }
    const handleResponse = (response: any): void => {
      if (response) {
        this.cartDetails = response;
        this.enrichTaxPricesWithDeliveryApplyOn();
        this.cartDetails.charges.forEach((charge) => {
          if (charge.title === "tipPrices") {
            this.selectedTipPriceIndex = this.tipPrices.indexOf(charge.price);
          }
        });
        if (this.cartDetails?.additionalPrices.length > 0) {
          this.cartDetails.additionalPrices = this.cartDetails.additionalPrices.map((item) => {
            const match = this.customPrices.find((c) => c._id === item.chargeId);
            return {
              ...item,
              title: match ? match.title : item.title
            };
          });
        }
        this.getCategoryGroups(this.merchantId);
      }
    };
    if (this.helper.route.url.includes("/users/create-booking/e-delivery")) {
      this.edeliveryService.getCart(parameters).subscribe(handleResponse);
    } else if (this.helper.route.url.includes("/users/create-booking/quick-commerce")) {
      this.qcommerceService.getCart(parameters).subscribe(handleResponse);
    } else {
      this.servicesService.getCart(parameters).subscribe(handleResponse);
    }
  }

  /**
   * Gets the cart items from local storage and calls the getCart method to get the cart details.
   * If the cart is empty, it sets the availableProducts property of the cartDetails object to an empty array.
   * @param tipPrices The tip price to pass to the getCart method.
   */
  getCartItems(tipPrices?: number): void {
    const cartItems = this.getCartItemsArray();
    this.merchantId = cartItems.length > 0 && cartItems[0].merchantId ? cartItems[0].merchantId : this.cartDetails?.merchantId;
    if (this.helper.route.url.includes("/users/create-booking/quick-commerce")) {
      this.merchantId = localStorage.getItem("merchantId") || "";
    }
    if (cartItems.length > 0) {
      this.getCart(cartItems, this.merchantId, tipPrices);
    } else {
      this.cartDetails.availableProducts = [];
      this.cartDetails.unavailableProducts = [];
    }
  }

  /**
   * Opens the cart modal component.
   * It subscribes to the bookingType observable of the EDeliveryService and sets the isDeliveryActive property
   * based on the value of the bookingType observable.
   * It also sets the tipPrices array based on the tipPrices property of the city setting stored in local storage.
   * Finally, it calls the getCartItems method to get the cart items and update the component's state.
   */
  // eslint-disable-next-line complexity, sonarjs/cognitive-complexity
  openCartModal(typeId?: string, entityType?: number, address?: string): void {
    this.offcanvasService.open(this.cartModal, { position: "end", panelClass: "modal-right-lg", backdrop: "static" });
    this.isCartModalOpen = true; // Set flag to true
    this.typeId = typeId ? typeId : "";
    this.entityType = entityType ? entityType : 0;
    this.isDeliveryBusiness = this.helper.route.url.includes("/users/create-booking/e-delivery");
    this.isServicesBusiness = this.helper.route.url.includes("/users/create-booking/services");
    this.cartItems = this.getCartItemsArray();
    this.merchantId = this.cartItems.length > 0 && this.cartItems[0].merchantId ? this.cartItems[0].merchantId : "";
    this.customerDetails = JSON.parse(sessionStorage.getItem("selectedUser") || "{}");
    this.currentDeliveryAddress = address ? address : JSON.parse(localStorage.getItem("currentDeliveryAddress") || "{}");
    this.citySetting = JSON.parse(localStorage.getItem("citySetting") || "{}");
    this.merchantSetting = JSON.parse(localStorage.getItem("merchantSetting") || "{}");
    this.serviceMainCategory =
      localStorage.getItem("cartServiceItemMainCategory") !== "undefined" ? JSON.parse(localStorage.getItem("cartServiceItemMainCategory") || "{}") : ({} as MainCategory);
    if (this.citySetting && this.citySetting.tipPrices) {
      this.tipPrices = this.citySetting.tipPrices;
    }
    if (this.helper.userToken) {
      this.helper.getDetails.subscribe((userDetails) => {
        this.availableWalletBalance = userDetails.credit;
      });
    }
    if (this.isDeliveryBusiness) {
      this.edeliveryService.bookingType$.subscribe((bookingType) => {
        if (this.merchantSetting && this.merchantSetting.bookingTypes.length > 1) {
          this.isDeliveryActive = bookingType === BookingType.DELIVERY ? true : false;
        } else {
          this.isDeliveryActive = this.merchantSetting?.bookingTypes?.[0] === BookingType.DELIVERY ? true : false;
        }
      });
    }
    if (this.isServicesBusiness) {
      this.servicesService.bookingType$.subscribe((bookingType) => {
        this.selectedBookingType = this.serviceMainCategory && this.serviceMainCategory.bookingTypes?.length === 1 ? this.serviceMainCategory.bookingTypes[0] : bookingType;
      });
      if (this.serviceMainCategory && this.serviceMainCategory.bookingTypes && this.serviceMainCategory.bookingTypes.length === 1) {
        this.selectedBookingType = this.serviceMainCategory.bookingTypes[0];
        this.servicesService.updateBookingType(this.selectedBookingType);
      }
    }
    this.setupPaymentOptions();
    this.checkBusiness();
  }

  /**
   * Increments the quantity of a product variant in the cart by 1.
   * If the product variant is not in the cart, it does not do anything.
   * @param product The product variant to increment the quantity of.
   */
  incrementProductQuantity(product: AvailableProduct): void {
    product.qty = product.qty === product.maxQtyAddInCart ? product.maxQtyAddInCart : product.qty + 1;
    const productModifiers =
      product.modifiers && product.modifiers.length > 0
        ? product.modifiers.map((category) => ({
            id: category.id,
            options: category.options.map((option) => ({
              id: option.id,
              price: option.price,
              qty: option.qty,
              name: option.name
            }))
          }))
        : [];
    const cartItems = this.getCartItemsArray();
    const cartService = this.getCartServiceByRoute();
    const cartItem = cartItems.find((item: Cart) => {
      if (item.modifiers && item.modifiers.length > 0) {
        return item.productId === product.productId && item.variantId === product.variantId && JSON.stringify(item.modifiers) === JSON.stringify(productModifiers);
      }
      return item.productId === product.productId && item.variantId === product.variantId;
    });
    if (cartItem) {
      cartService?.updateQuantity(product.productId, cartItem, cartItem.qty + 1, product.maxQtyAddInCart);
    }
  }

  /**
   * Decrement the quantity of a product variant in the cart by 1.
   * If the current quantity is already 1, remove the product variant from the cart.
   * @param product The product variant to decrement the quantity of.
   */
  decrementProductQuantity(product: AvailableProduct): void {
    product.qty -= 1;
    const productModifiers =
      product.modifiers && product.modifiers.length > 0
        ? product.modifiers.map((category) => ({
            id: category.id,
            options: category.options.map((option) => ({
              id: option.id,
              price: option.price,
              qty: option.qty,
              name: option.name
            }))
          }))
        : [];
    const cartItems = this.getCartItemsArray();
    const cartService = this.getCartServiceByRoute();
    const cartItem = cartItems.find((item: Cart) => {
      if (item.modifiers && item.modifiers.length > 0) {
        return item.productId === product.productId && item.variantId === product.variantId && JSON.stringify(item.modifiers) === JSON.stringify(productModifiers);
      }
      return item.productId === product.productId && item.variantId === product.variantId;
    });
    if (cartItem) {
      if (cartItem.qty > 1) {
        cartService?.updateQuantity(product.productId, cartItem, cartItem.qty - 1, product.maxQtyAddInCart);
      } else {
        cartService?.removeFromCart(product.productId, cartItem);
      }
    }
    if (JSON.parse(localStorage.getItem("deliveryCartItems") || "[]").length === 0) {
      this.edeliveryService.updateCartItems([]);
    } else if (JSON.parse(localStorage.getItem("cartItems") || "[]").length === 0) {
      this.qcommerceService.updateCartItems([]);
    } else if (JSON.parse(localStorage.getItem("serviceCartItems") || "[]").length === 0) {
      this.servicesService.updateCartItems([]);
    }
  }

  /**
   * Updates the selected tip price index and recalculates the cart items based on the chosen tip.
   * Clears any manual tip input if a preset tip is selected. If the chosen tip is already selected,
   * it deselects the tip by setting the index to -1 and recalculates the cart without the tip.
   * Otherwise, it sets the selected tip price index to the provided index and recalculates the cart
   * with the new tip amount.
   *
   * @param price - The tip amount to apply.
   * @param tipPriceIndex - The index of the tip in the preset tip prices array.
   */
  addTip(price: number, tipPriceIndex: number): void {
    this.manualTipAmount = null; // Clear manual input when preset is selected
    if (this.tipPrices[this.selectedTipPriceIndex] === price) {
      this.selectedTipPriceIndex = -1;
      this.getCartItems();
    } else {
      this.selectedTipPriceIndex = tipPriceIndex;
      this.getCartItems(price);
    }
  }

  /**
   * Applies the manual tip amount to the cart and recalculates the cart items.
   * If the manual tip amount is valid (i.e., greater than 0), it will deselect any
   * preset tip options and call `getCartItems` with the manual tip amount.
   */
  applyManualTip(): void {
    if (this.manualTipAmount && this.manualTipAmount > 0) {
      this.selectedTipPriceIndex = -1; // Deselect preset tip options
      this.getCartItems(this.manualTipAmount);
    }
  }

  /**
   * Clears the manual tip input field and resets the selected tip price index.
   * When called, it will also recalculate the cart items.
   */
  clearManualTip(): void {
    this.manualTipAmount = null;
    this.selectedTipPriceIndex = -1;
    this.getCartItems();
  }

  /**
   * Resets the selected tip price index when the manual tip input field is edited.
   * This ensures that when the user is typing a custom tip amount, no preset tip
   * option is selected.
   */
  onTipInputChange(): void {
    this.selectedTipPriceIndex = -1; // Ensure no preset is selected when typing
  }

  /**
   * Toggles the cart to delivery mode.
   * Sets isDeliveryActive to true, updates the booking type to 5 (delivery),
   * and retrieves the cart items again with the currently selected tip amount.
   */
  toggleDelivery(): void {
    this.isDeliveryActive = true;
    this.edeliveryService.updateBookingType(BookingType.DELIVERY);
    this.appliedPromo = {} as PromoCode;
    this.setupPaymentOptions();
    this.getCartItems(this.tipPrices[this.selectedTipPriceIndex]);
  }

  /**
   * Toggles the cart to pickup mode.
   * Sets isDeliveryActive to false, updates the booking type to 6 (pickup),
   * clears the applied promo code, and retrieves the cart items again.
   */
  togglePickup(): void {
    this.isDeliveryActive = false;
    this.edeliveryService.updateBookingType(BookingType.PICKUP);
    this.appliedPromo = {} as PromoCode;
    this.setupPaymentOptions();
    this.getCartItems();
  }

  toggleServiceBookingType(bookingType: BookingType): void {
    this.selectedBookingType = bookingType;
    this.servicesService.updateBookingType(this.selectedBookingType);
    this.appliedPromo = {} as PromoCode;
    this.setupPaymentOptions();
    this.getCartItems();
  }

  /**
   * Places an order with the current cart items and user details.
   * Retrieves cart items and delivery address from local storage,
   * constructs an order parameter object, and calls the eDeliveryService
   * to place the order. Upon successful order placement, it clears the
   * applied promo code, closes the cart modal, removes the cart items
   * from local storage, and resets the cart items observable.
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity, complexity
  placeOrder(customerDetails: any): void {
    if (customerDetails && customerDetails.firstName) {
      if (!this.selectedPaymentMethod) {
        this.helper.toasterService.showNotification("error", this.helper.translateService.instant("validation-title.please-select-payment-method"));
        return;
      }
      const cartItems = this.getCartItemsArray();
      let bookingTime = Number(localStorage.getItem("bookingTime"));
      if (this.isDeliveryBusiness && bookingTime && this.merchantSetting && this.merchantSetting.deliveryScheduleSetting.isActive) {
        bookingTime = new Date(new Date(new Date().getTime() + this.merchantSetting.deliveryScheduleSetting.userScheduleBuffer * 60000)).getTime();
      }
      const deliveryAddress = { ...this.currentDeliveryAddress };
      delete deliveryAddress.type;
      if (this.helper.route.url.includes("/users/create-booking/quick-commerce") || this.helper.route.url.includes("/users/create-booking/services")) {
        cartItems.forEach((product) => {
          delete product.price;
          delete product.serviceMerchantId;
        });
      }
      const parameters: any = {
        destinationAddress: deliveryAddress,
        products: cartItems,
        paymentMode: this.selectedPaymentMethod,
        bookingTime: bookingTime || Date.now()
      };
      if (this.isServicesBusiness) {
        parameters["bookingType"] = this.selectedBookingType;
        parameters["businessType"] = BusinessType.SERVICE;
      } else if (this.isDeliveryBusiness) {
        parameters["bookingType"] = this.isDeliveryActive ? BookingType.DELIVERY : BookingType.PICKUP;
        parameters["businessType"] = BusinessType.DELIVERY;
      } else {
        parameters["bookingType"] = BookingType.QUICK_COMMERCE;
        parameters["businessType"] = BusinessType.QUICK_COMMERCE;
      }
      if ((this.isDeliveryActive && this.customerNote) || (this.selectedBookingType === this.helper.BookingType.SERVICE_AT_HOME && this.customerNote)) {
        parameters["customerNote"] = this.customerNote;
      }
      if (this.selectedPaymentMethod === this.helper.PaymentGatewayEnum.Stripe) {
        this.loadStripeKey();
      }
      parameters["customerDetail"] = customerDetails;
      parameters["isBookForOther"] = true;
      if (this.appliedPromo && this.appliedPromo.code) {
        parameters["promoCode"] = this.appliedPromo.code;
      }
      if (this.isServicesBusiness) {
        const cartServiceMerchantDetails: Merchant = JSON.parse(localStorage.getItem("cartServiceMerchantDetails") || "{}");
        parameters["mainCategoryId"] = this.serviceMainCategory._id;
        parameters["cityId"] = this.citySetting.cityId;
        if (this.selectedBookingType === this.helper.BookingType.ON_SITE_SERVICE) {
          parameters["isPickupFromHome"] = this.isPickAtMyLocation;
          parameters["isDropAtHome"] = this.isDropAtMyLocation;
        }
        if (cartServiceMerchantDetails && cartServiceMerchantDetails._id) {
          parameters["typeId"] = cartServiceMerchantDetails._id;
          parameters["entityType"] = this.helper.UserType.MERCHANT;
          parameters["isMerchantService"] = true;
        }
        if (this.entityType && this.typeId) {
          parameters["entityType"] = this.entityType;
          parameters["merchantId"] = this.typeId;
          parameters["typeId"] = this.typeId;
        }
      }
      if (this.isDeliveryBusiness) {
        parameters["merchantId"] = cartItems[0].merchantId ? cartItems[0].merchantId : "";
      } else if (!this.isServicesBusiness) {
        parameters["merchantId"] = localStorage.getItem("merchantId");
      }
      if (
        (this.isDeliveryActive || this.helper.route.url.includes("/users/create-booking/quick-commerce") || this.helper.route.url.includes("/users/create-booking/services")) &&
        (this.tipPrices[this.selectedTipPriceIndex] || this.manualTipAmount)
      ) {
        parameters["tipPrice"] = this.tipPrices[this.selectedTipPriceIndex] || this.manualTipAmount;
      }
      if (this.helper.route.url.includes("/users/create-booking/e-delivery")) {
        this.edeliveryService.placeOrder(parameters).subscribe((res) => {
          if (res) {
            if (res.data && res.data.data) {
              this.paymentService.amountCreatedPayment(res.data.data);
            }
            if (this.helper.PaymentGatewayEnumArray.includes(this.selectedPaymentMethod)) {
              this.paymentService.paymentCompletedObservable.subscribe((isCompleted) => {
                if (isCompleted) {
                  localStorage.removeItem("deliveryCartItems");
                  this.edeliveryService.updateCartItems([]);
                  this.closeModal();
                }
              });
            } else {
              localStorage.removeItem("deliveryCartItems");
              this.edeliveryService.updateCartItems([]);
              this.closeModal();
            }
          }
        });
      } else if (this.helper.route.url.includes("/users/create-booking/quick-commerce")) {
        this.qcommerceService.placeOrder(parameters).subscribe((res) => {
          if (res) {
            if (res.data && res.data.data) {
              this.paymentService.amountCreatedPayment(res.data.data);
            }
            if (this.helper.PaymentGatewayEnumArray.includes(this.selectedPaymentMethod)) {
              this.paymentService.paymentCompletedObservable.subscribe((isCompleted) => {
                if (isCompleted) {
                  localStorage.removeItem("cartItems");
                  this.qcommerceService.updateCartItems([]);
                  this.closeModal();
                }
              });
            } else {
              localStorage.removeItem("cartItems");
              this.qcommerceService.updateCartItems([]);
              this.closeModal();
            }
          }
        });
      } else if (this.isServicesBusiness) {
        this.servicesService.placeOrder(parameters).subscribe((res) => {
          if (res) {
            if (res.data && res.data.data) {
              this.paymentService.amountCreatedPayment(res.data.data);
            }
            if (this.helper.PaymentGatewayEnumArray.includes(this.selectedPaymentMethod)) {
              this.paymentService.paymentCompletedObservable.subscribe((isCompleted) => {
                if (isCompleted) {
                  localStorage.removeItem("serviceCartItems");
                  this.servicesService.updateCartItems([]);
                  this.closeModal();
                }
              });
            } else {
              localStorage.removeItem("serviceCartItems");
              this.servicesService.updateCartItems([]);
              this.closeModal();
            }
          }
        });
      }
    }
  }

  /**
   * Closes the cart modal and clears the applied promo code.
   */
  closeModal(): void {
    this.isCartModalOpen = false;
    this.appliedPromo = {} as PromoCode;
    this.offcanvasService.dismiss(this.cartModal);
  }

  /**
   * Navigates to the merchant details page to add more products.
   * Updates the selected cart merchant and stores the merchant details
   * in local storage. Clears the applied promo code and closes the cart modal.
   */
  addMoreProducts(businessType: BusinessType): void {
    if (businessType === BusinessType.DELIVERY) {
      this.edeliveryService.updateSelectedCartMerchant(this.cartDetails.merchantDetail);
      localStorage.setItem("merchantDetails", JSON.stringify(this.cartDetails.merchantDetail));
      this.closeModal();
      this.helper.route.navigate(["/users/create-booking/e-delivery/merchant-details"]);
    } else {
      this.servicesService.updateSelectedCartMerchant(this.cartDetails.merchantDetail);
      localStorage.setItem("serviceMerchantDetails", JSON.stringify(this.cartDetails.merchantDetail));
      this.closeModal();
      this.helper.route.navigate(["/users/create-booking/services/merchant-details"]);
    }
  }

  /**
   * Opens the promo code modal.
   * @param event The event object.
   */
  openPromoCodeModal(event: any): void {
    event.stopPropagation();
    const cartItems = this.getCartItemsArray();
    const countrySetting = JSON.parse(localStorage.getItem("countrySetting") || "{}");
    const bookingTime = Number(localStorage.getItem("bookingTime"));
    const bookingData = {
      countryId: countrySetting._id,
      cityId: this.citySetting.cityId,
      paymentMethod: this.selectedPaymentMethod,
      latitude: this.currentDeliveryAddress.latitude,
      longitude: this.currentDeliveryAddress.longitude,
      bookingTime: bookingTime || Date.now(),
      products: cartItems
    };
    if (this.helper.route.url.includes("/users/create-booking/e-delivery")) {
      (bookingData as any).businessType = BusinessType.DELIVERY;
      (bookingData as any).merchantId = cartItems[0].merchantId;
      (bookingData as any).priceMode = this.isDeliveryActive ? BookingType.DELIVERY : BookingType.PICKUP;
    } else if (this.helper.route.url.includes("/users/create-booking/quick-commerce")) {
      (bookingData as any).businessType = BusinessType.QUICK_COMMERCE;
      (bookingData as any).merchantId = localStorage.getItem("merchantId");
      (bookingData as any).priceMode = BookingType.QUICK_COMMERCE;
    } else {
      (bookingData as any).mainCategoryId = this.serviceMainCategory._id;
      (bookingData as any).businessType = BusinessType.SERVICE;
      (bookingData as any).priceMode = this.selectedBookingType;
    }
    this.promoCodeModal.openModal(bookingData);
  }

  /**
   * Handles promo code application events.
   * @param event The event object.
   */
  handlePromoApplied(event: any): void {
    if (event) {
      this.appliedPromo = event;
      this.getCartItems(this.tipPrices[this.selectedTipPriceIndex]);
    }
  }

  /**
   * Removes the applied promo code.
   */
  removeAppliedPromo(): void {
    this.appliedPromo = {} as PromoCode;
    this.getCartItems(this.tipPrices[this.selectedTipPriceIndex]);
  }

  /**
   * Opens the booking payment modal.
   */
  openBookingPaymentModal(): void {
    let amount: number = 0;
    let selectedBookingTypeKey: string;
    if (this.isDeliveryBusiness) {
      selectedBookingTypeKey = this.isDeliveryActive ? "DELIVERY" : "PICKUP";
    } else if (this.isServicesBusiness) {
      selectedBookingTypeKey = this.helper.BookingType[this.selectedBookingType];
    } else {
      selectedBookingTypeKey = "QUICK_COMMERCE";
    }
    const paymentMethod = { key: this.selectedPaymentMethod, string: this.helper.PaymentModeStrings[this.selectedPaymentMethod] };
    amount = this.cartDetails.total;
    const cityPaymentSetting = this.citySetting.paymentSetting[selectedBookingTypeKey];
    this.bookingPaymentModal.openModal(amount, paymentMethod, cityPaymentSetting);
  }

  /**
   * Handles booking payment method selection events.
   * @param event The selected payment method.
   */
  bookingSelectPaymentMethod(event: any): void {
    this.selectedPaymentMethod = event;
    this.onSelectPaymentMethod(event);
  }

  private setupPaymentOptions(): void {
    let selectedBookingTypeKey: string;
    if (this.isDeliveryBusiness) {
      selectedBookingTypeKey = this.isDeliveryActive ? "DELIVERY" : "PICKUP";
    } else if (this.isServicesBusiness) {
      selectedBookingTypeKey = this.helper.BookingType[this.selectedBookingType];
    } else {
      selectedBookingTypeKey = "QUICK_COMMERCE";
    }
    const paymentSettings = this.citySetting.paymentSetting[selectedBookingTypeKey] || {};
    this.paymentOptions = [];
    this.isPaymentOptionError = false;

    if (paymentSettings.isCash) {
      this.paymentOptions.push({ key: this.helper.PaymentMode.Cash, value: this.helper.PaymentModeStrings[this.helper.PaymentMode.Cash] });
    }

    if (paymentSettings.isWallet) {
      this.paymentOptions.push({ key: this.helper.PaymentMode.Wallet, value: this.helper.PaymentModeStrings[this.helper.PaymentMode.Wallet] });
    }

    paymentSettings.paymentGateways?.forEach((gateway: number) => {
      if (this.helper.PaymentModeStrings[gateway]) {
        this.paymentOptions.push({ key: gateway, value: this.helper.PaymentModeStrings[gateway] });
      }
    });
    if (paymentSettings.isCash) {
      this.onSelectPaymentMethod(this.paymentOptions[0]?.key);
    }
  }

  /**
   * Handles payment method selection events.
   * @param event The event object.
   */
  async onSelectPaymentMethod(event: any): Promise<void> {
    await this.getCardList();

    this.selectedPaymentMethod = event;
    this.getCartItems(this.tipPrices[this.selectedTipPriceIndex]);
    this.isPaymentOptionError = false;

    const isStripeOrPaystack = this.helper.PaymentGatewayCardEnumArray.includes(this.selectedPaymentMethod);

    if (isStripeOrPaystack) {
      this.isPaymentOptionError = !this.cards.some((card) => card.paymentGatewayType === this.selectedPaymentMethod);
    }

    if (this.appliedPromo && this.appliedPromo.code) {
      this.appliedPromo = {} as PromoCode;
    }
  }

  /**
   * Gets the card list.
   * @returns A promise that resolves with the card list.
   */
  getCardList(): Promise<any> {
    if (!this.helper.userToken) {
      return Promise.resolve([]);
    }
    const isStripeOrPaystack = this.helper.PaymentGatewayCardEnumArray.includes(this.selectedPaymentMethod);
    if (!isStripeOrPaystack && this.selectedPaymentMethod) {
      return Promise.resolve([]);
    }
    const paymentGatewayArray = this.helper.PaymentGatewayCardEnumArray.toString();
    const params = { countryId: this.helper.dispatcherDetails?.entity?.countryId, paymentGatewayTypes: paymentGatewayArray, isShowErrorToast: false };

    return new Promise((resolve) => {
      this.paymentService.getCardList(params).subscribe((res: any) => {
        this.cards = [];
        if (res) {
          this.cards = res.cards;
          resolve(true);
        }
        resolve(false);
      });
    });
  }

  /**
   * Loads the Stripe key for payment processing.
   */
  loadStripeKey(): void {
    const capturePaymentIntent: any = localStorage.getItem("capturePaymentIntent");
    if (capturePaymentIntent) {
      const parsedCapturePaymentIntent = capturePaymentIntent ? JSON.parse(capturePaymentIntent) : null;
      const publicKeyForStripe = parsedCapturePaymentIntent.data.data?.intent.publicKey;
      this.helper.loadStripe(publicKeyForStripe);
    } else {
      this.paymentService.getPaymentGetway({ countryId: this.helper.dispatcherDetails?.entity?.countryId }).subscribe((response: any) => {
        if (response) {
          const paymentGatewaysList = response?.paymentGateways;
          if (this.selectedPaymentMethod === this.helper.PaymentGatewayType[this.helper.PaymentGatewayEnum.Stripe]) {
            const paytmentGateway = paymentGatewaysList.filter((x: any) => x.type === this.selectedPaymentMethod);
            this.helper.loadStripe(paytmentGateway[0]?.credential?.publicKey);
          }
        }
      });
    }
  }

  /**
   * Toggles the visibility of the discount details.
   * @returns {void}
   */
  toggleOfferVisibility(): void {
    this.isShowDiscountDetails = !this.isShowDiscountDetails;
  }

  toggleDeliveryPriceVisibility(): void {
    this.isShowDeliveryPriceDetails = !this.isShowDeliveryPriceDetails;
  }

  getAllUsers(): any[] {
    return JSON.parse(sessionStorage.getItem("otherUsers") || "[]");
  }

  /**
   * Checks if a business is available for ordering.
   * Checks if a business is available for ordering using the ApiService.
   * Subscribes to the service's observable to receive the response data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   */
  checkBusiness(): void {
    const parameters: any = {
      destinationAddress: this.currentDeliveryAddress
    };
    if (this.isDeliveryBusiness) {
      parameters["merchantId"] = this.merchantId ? this.merchantId : "";
      this.edeliveryService.checkDeliveryBusiness(parameters).subscribe((res) => {
        if (res) {
          this.getCustomPrices();
          this.merchantSetting = res.merchantSetting;
          this.citySetting = res.citySetting;
          this.helper.currencySign = res.countrySetting.currencySign;
          localStorage.setItem("citySetting", JSON.stringify(res.citySetting));
          localStorage.setItem("merchantSetting", JSON.stringify(res.merchantSetting));
          this.edeliveryService.updateMerchantSetting(res.merchantSetting);
          if (this.merchantSetting && this.merchantSetting.bookingTypes.length === 1) {
            this.isDeliveryActive = this.merchantSetting.bookingTypes[0] === BookingType.DELIVERY ? true : false;
            this.edeliveryService.updateBookingType(this.merchantSetting.bookingTypes[0]);
          }
          this.getCartItems();
        } else {
          this.closeModal();
        }
      });
    } else if (this.isServicesBusiness) {
      parameters["mainCategoryId"] = this.serviceMainCategory._id;
      parameters["entityType"] = this.entityType;
      parameters["typeId"] = this.typeId;
      this.servicesService.checkServiceBusiness(parameters).subscribe((res) => {
        if (res) {
          this.getCustomPrices();
          this.citySetting = res.citySetting;
          this.helper.currencySign = res.countrySetting.currencySign;
          localStorage.setItem("citySetting", JSON.stringify(res.citySetting));
          this.merchantSetting = res.merchantSetting || null;
          localStorage.setItem("serviceMerchantSetting", JSON.stringify(this.merchantSetting));
          this.getCartItems();
        } else {
          this.closeModal();
        }
      });
    } else {
      this.qcommerceService.checkQuickCommerceBusiness(parameters).subscribe((res) => {
        if (res) {
          this.getCustomPrices();
          this.citySetting = res.citySetting;
          this.helper.currencySign = res.countrySetting.currencySign;
          localStorage.setItem("citySetting", JSON.stringify(res.citySetting));
          this.getCartItems();
        } else {
          this.closeModal();
        }
      });
    }
  }

  formatModifiers(item: any): string {
    if (!item.modifiers || item.modifiers.length === 0) return "";
    return item.modifiers.map((modifier: Modifier) => modifier.options.map((option) => (option.qty > 1 ? `${option.name} x ${option.qty}` : option.name)).join(", ")).join(", ");
  }

  getCategoryGroups(merchantId: string): void {
    const parameters: any = {
      groupFor: "CART",
      isShowSuccessToast: false,
      isShowErrorToast: false,
      page: 1,
      limit: 15
    };
    if (this.isDeliveryBusiness) {
      parameters["businessType"] = BusinessType.DELIVERY;
    } else if (this.isServicesBusiness) {
      parameters["businessType"] = BusinessType.SERVICE;
    } else {
      parameters["businessType"] = BusinessType.QUICK_COMMERCE;
    }
    if (!this.isServicesBusiness) {
      parameters["merchantId"] = merchantId ? merchantId : "";
    }
    if (this.isServicesBusiness) {
      parameters["cityId"] = this.citySetting.cityId ? this.citySetting.cityId : "";
      parameters["mainCategoryId"] = this.serviceMainCategory._id;
    }
    this.qcommerceService.getGroupList(parameters).subscribe((res) => {
      if (res.groups?.length > 0) {
        this.getTags();
        this.categoryGroups = res.groups;
        this.categoryGroups.forEach((categoryGroup) =>
          categoryGroup.childs.forEach((child) => (child.highlightedTags = child.highlightedTagIds ? this.tags.filter((tag) => child.highlightedTagIds!.includes(tag._id)) : []))
        );
      } else {
        this.categoryGroups = [];
      }
    });
  }

  getTags(): void {
    const parameters: any = { type: "TAG", isShowSuccessToast: false, isShowErrorToast: false };
    this.qcommerceService.getTags(parameters).subscribe((res) => {
      if (res.tags?.length > 0) {
        this.tags = res.tags;
      } else {
        this.tags = [];
      }
    });
  }

  /**
   * Checks if the current booking type allows promo codes to be applied.
   * For DELIVERY and PICKUP bookings, this depends on the city setting for the booking type.
   * For QUICK_COMMERCE bookings, this depends on the city setting for QUICK_COMMERCE.
   * @returns {boolean} true if promo codes are allowed, false otherwise.
   */
  isAllowPromoCode(): boolean {
    if (this.citySetting) {
      if (this.isDeliveryBusiness) {
        return this.isDeliveryActive ? this.citySetting.bookingSetting.DELIVERY.includes("AllowPromocode") : this.citySetting.bookingSetting.PICKUP.includes("AllowPromocode");
      } else if (this.helper.route.url.includes("/users/create-booking/quick-commerce")) {
        return this.citySetting.bookingSetting.QUICK_COMMERCE.includes("AllowPromocode");
      }
      return this.citySetting.bookingSetting[this.helper.BookingType[this.selectedBookingType]].includes("AllowPromocode");
    }
    return false;
  }

  /**
   * Returns true if the current booking type allows tip to be added.
   * Tip is allowed if the booking type is DELIVERY or PICKUP and the city setting includes "Tip" in the booking setting for the booking type,
   * or if the booking type is QUICK_COMMERCE and the city setting includes "Tip" in the booking setting for QUICK_COMMERCE.
   * @returns {boolean} True if the booking type allows tip to be added, false otherwise.
   */
  isAllowTip(): boolean {
    if (this.citySetting) {
      if (this.isDeliveryBusiness) {
        return this.isDeliveryActive ? this.citySetting.bookingSetting.DELIVERY.includes("Tip") : this.citySetting.bookingSetting.PICKUP.includes("Tip");
      } else if (this.helper.route.url.includes("/users/create-booking/quick-commerce")) {
        return this.citySetting.bookingSetting.QUICK_COMMERCE.includes("Tip");
      }
      return this.citySetting.bookingSetting[this.helper.BookingType[this.selectedBookingType]].includes("Tip");
    }
    return false;
  }

  isAllowBookForOther(): boolean {
    if (this.citySetting) {
      let bookingType: string = "";
      if (this.isDeliveryBusiness) {
        bookingType = this.isDeliveryActive ? "DELIVERY" : "PICKUP";
      } else if (this.helper.route.url.includes("/users/create-booking/quick-commerce")) {
        bookingType = "QUICK_COMMERCE";
      } else {
        bookingType = this.selectedBookingType;
      }
      return this.citySetting?.bookingSetting[bookingType]?.includes("AllowRideForOther") ?? false;
    }
    return false;
  }

  /**
   * Removes unavailable products from the cart.
   * Gets the current cart items from localStorage, filters out unavailable products, and updates localStorage with the new cart items.
   * Then calls getCartItems to update the cart details.
   */
  removeUnavailableProducts(): void {
    // Get cart items from localStorage
    let cartItems = this.getCartItemsArray();
    // Filter out unavailable products
    cartItems = cartItems.filter(
      (item: Cart) => !this.cartDetails.unavailableProducts.some((unavailable) => unavailable.productId === item.productId && unavailable.variantId === item.variantId)
    );

    // Update localStorage
    if (this.helper.route.url.includes("/users/create-booking/quick-commerce")) {
      localStorage.setItem("cartItems", JSON.stringify(cartItems));
      this.qcommerceService.updateCartItems(cartItems);
    } else if (this.helper.route.url.includes("/users/create-booking/services")) {
      localStorage.setItem("serviceCartItems", JSON.stringify(cartItems));
      this.servicesService.updateCartItems(cartItems);
    } else {
      localStorage.setItem("deliveryCartItems", JSON.stringify(cartItems));
      this.edeliveryService.updateCartItems(cartItems);
    }
    this.getCartItems(this.tipPrices[this.selectedTipPriceIndex]);
  }

  /**
   * Handles changes to the "Drop At My Location" checkbox.
   * Triggers a recalculation of cart items with the currently selected tip amount.
   */
  onDropAtMyLocationChange(): void {
    this.getCartItems(this.tipPrices[this.selectedTipPriceIndex]);
  }

  /**
   * Handles changes to the "Pick At My Location" checkbox.
   * Triggers a recalculation of cart items with the currently selected tip amount.
   */
  onPickAtMyLocationChange(): void {
    this.getCartItems(this.tipPrices[this.selectedTipPriceIndex]);
  }

  /**
   * Gets the current cart items from localStorage based on business type.
   * If the cart items are not found in localStorage, it returns an empty array.
   * @returns The current cart items as an array of Cart objects.
   */
  private getCartItemsArray(): Cart[] {
    if (this.helper.route.url.includes("/users/create-booking/quick-commerce")) {
      return JSON.parse(localStorage.getItem("cartItems") || "[]");
    } else if (this.helper.route.url.includes("/users/create-booking/services")) {
      return JSON.parse(localStorage.getItem("serviceCartItems") || "[]");
    }
    return JSON.parse(localStorage.getItem("deliveryCartItems") || "[]");
  }

  changeServiceProvider(): void {
    this.offcanvasService.dismiss(this.cartModal);
    this.onChangeServiceProvider.emit();
  }

  /**
   * Handel error when image is not loaded and gets error
   */
  handleImageError(event: any): void {
    event.target.src = this.helper.DefaultImages.DefaultProductImages;
  }

  /**
   * Closes the cart modal and emits an event to add or select an address.
   * The event is handled by the parent component which will open the address modal.
   */
  addOrSelectAddress(): void {
    this.onAddOrSelectAddress.emit(this.currentDeliveryAddress);
  }

  /**
   * Handles clicks on the address field in the cart modal.
   * If the user is logged in and the business supports delivery or quick commerce,
   * or if the user is logged in and the selected booking type is SERVICE_AT_HOME,
   * it emits an event to the parent component to open the address modal.
   */
  handleAddressClick(): void {
    if (
      (this.helper.userToken && this.isDeliveryBusiness && this.isDeliveryActive) ||
      (this.isServicesBusiness && (this.selectedBookingType === this.helper.BookingType.SERVICE_AT_HOME || this.isDropAtMyLocation || this.isPickAtMyLocation)) ||
      this.helper.route.url.includes("/users/create-booking/quick-commerce")
    ) {
      this.addOrSelectAddress();
    }
  }

  /**
   * Opens the modal for adding or selecting customer details.
   * Utilizes the bookForOtherModal component to display the modal.
   */
  openCustomerDetailsModal(): void {
    this.bookForOtherModal.openModal();
  }

  private getCartServiceByRoute(): EDeliveryService | QCommerceService | ServicesService | null {
    if (this.isDeliveryBusiness) return this.edeliveryService;
    if (this.isServicesBusiness) return this.servicesService;
    return this.qcommerceService;
  }

  getCustomPrices(): void {
    this.accessibilityService.getAccessibilityList().subscribe((response: any) => {
      if (response) {
        this.customPrices = response.customPrices;
      }
    });
  }

  enrichTaxPricesWithDeliveryApplyOn(): void {
    const deliveryCharge = this.cartDetails.charges.find((charge) => charge.title === this.helper.PriceParamEnum.deliveryPrice);
    const deliveryChildTitles = deliveryCharge?.childs?.map((child) => child.title) || [];
    this.cartDetails.taxPrices = this.cartDetails.taxPrices.map((tax) => {
      const filteredApplyOn = (tax.applyOn || []).filter((title: string) => deliveryChildTitles.includes(title));
      return {
        ...tax,
        filteredAppliedOn: filteredApplyOn
      };
    });
  }

  ngOnDestroy(): void {
    this.cartItemSubscription?.unsubscribe();
  }
}
